// KeywordCategories.jsx - Enhanced with Stats & Suggestions
// Version 3.0 - Added occurrences modal

import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Search, Edit, Trash2, X, Check, Loader2, Key, List,
  ChevronDown, ChevronRight, RefreshCw, Phone, PhoneOff, Play, Pause,
  Copy, AlertTriangle, Eye, EyeOff, Zap, Hash, Lock, Filter,
  Lightbulb, Sparkles, Clock, Target, TrendingUp,
  MessageSquare, ExternalLink, FileText, Info, Volume2
} from 'lucide-react'
import { API_BASE_URL, keywordApi } from '../services/api'
import './KeywordCategories.css'

const RECENT_DAYS = 7
const KEYWORDS_INITIAL_SHOW = 15

// Format large numbers: 1000 -> 1k, 1500000 -> 1.5m
function formatCount(num) {
  if (!num || num < 1000) return num?.toString() || '0'
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'm'
  if (num >= 10000) return Math.round(num / 1000) + 'k'
  return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
}

const DISPOSITION_OPTIONS = [
  { value: 'do-not-call', label: 'DNC (Do Not Call)' },
  { value: 'not-interested', label: 'NI (Not Interested)' },
  { value: 'answering-machine', label: 'AM (Answering Machine)' },
  { value: 'call-back', label: 'CB (Call Back)' },
  { value: 'no-answer', label: 'NA (No Answer)' }
]

function KeywordCategories() {
  const navigate = useNavigate()

  // Data
  const [categories, setCategories] = useState([])
  const [duplicateKeywords, setDuplicateKeywords] = useState(new Map())

  // Stats data (NEW)
  const [keywordStats, setKeywordStats] = useState({})
  const [suggestions, setSuggestions] = useState({ high: [], medium: [], low: [] })
  const [loadingStats, setLoadingStats] = useState(false)

  // Occurrences Modal (NEW)
  const [showOccurrencesModal, setShowOccurrencesModal] = useState(false)
  const [occurrencesKeyword, setOccurrencesKeyword] = useState('')
  const [occurrencesData, setOccurrencesData] = useState([])
  const [occurrencesMeta, setOccurrencesMeta] = useState(null)
  const [loadingOccurrences, setLoadingOccurrences] = useState(false)

  // Audio player for occurrences
  const [playingRecording, setPlayingRecording] = useState(null)
  const [audioLoading, setAudioLoading] = useState(false)
  const audioRef = useRef(null)

  // UI State
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('categories') // 'categories' or 'suggestions'
  const [expandedCategoryId, setExpandedCategoryId] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [keywordSearchTerm, setKeywordSearchTerm] = useState('')
  const [showAllKeywords, setShowAllKeywords] = useState({})
  const [showSuggestions, setShowSuggestions] = useState(true)

  // Suggestions tab filters
  const [suggestionsSearch, setSuggestionsSearch] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [suggestionsSortBy, setSuggestionsSortBy] = useState('occurrences')
  const [suggestionsMeta, setSuggestionsMeta] = useState({})

  // Filters
  const [actionFilter, setActionFilter] = useState('all')
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false)
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState('asc')

  // Category Modal
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [categoryForm, setCategoryForm] = useState({
    name: '', description: '', action: 'continue', disposition_code: '', threshold: 6, background_check: false
  })
  const [saving, setSaving] = useState(false)

  // Inline Keyword Editing
  const [editingKeywordId, setEditingKeywordId] = useState(null)
  const [editingKeywordText, setEditingKeywordText] = useState('')
  const [editingKeywordScore, setEditingKeywordScore] = useState(1)

  // Inline Keyword Adding
  const [addingKeywordToCategoryId, setAddingKeywordToCategoryId] = useState(null)
  const [newKeywordText, setNewKeywordText] = useState('')
  const [newKeywordScore, setNewKeywordScore] = useState(5)

  // Add suggestion to category (NEW)
  const [addingSuggestion, setAddingSuggestion] = useState(null)
  const [suggestionTargetCategory, setSuggestionTargetCategory] = useState(null)
  const [suggestionScore, setSuggestionScore] = useState(5)
  const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set())

  // Delete confirmation modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, category: null })
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteError, setDeleteError] = useState('')

  const newKeywordInputRef = useRef(null)
  const DELETE_PASSWORD = '474747'

  useEffect(() => {
    loadCategories()
    loadStats()
  }, [])

  // ========== DATA LOADING ==========
  const loadCategories = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/keyword-categories`)
      const data = await res.json()
      setCategories(Array.isArray(data) ? data : [])
      buildDuplicateMap(data)
    } catch (error) {
      console.error('Failed to load categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    setLoadingStats(true)
    try {
      const [statsRes, suggestionsRes] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/keyword-categories/stats?days=7`),
        fetch(`${API_BASE_URL}/keyword-categories/suggestions?days=7&min_occurrences=10`)
      ])

      if (statsRes.status === 'fulfilled') {
        const data = await statsRes.value.json()
        if (data.success) {
          setKeywordStats(data.keyword_stats || {})
        }
      }

      if (suggestionsRes.status === 'fulfilled') {
        const data = await suggestionsRes.value.json()
        if (data.success) {
          setSuggestions(data.suggestions || { high: [], medium: [], low: [] })
          setSuggestionsMeta({
            total: data.total_suggestions || 0,
            transcriptions: data.transcriptions_analyzed || 0,
            period: data.period_days || 7,
            existing: data.existing_keywords_count || 0,
            dismissed: data.dismissed_count || 0
          })
        }
      }
    } catch (error) {
      console.error('Failed to load stats:', error)
    } finally {
      setLoadingStats(false)
    }
  }

  // ========== OCCURRENCES MODAL (NEW) ==========
  const loadOccurrences = async (keyword) => {
    setOccurrencesKeyword(keyword)
    setShowOccurrencesModal(true)
    setLoadingOccurrences(true)
    setOccurrencesData([])
    setOccurrencesMeta(null)

    try {
      const res = await fetch(`${API_BASE_URL}/keyword-categories/occurrences?keyword=${encodeURIComponent(keyword)}&days=7&limit=200`)
      const data = await res.json()
      if (data.success) {
        setOccurrencesData(data.data || [])
        setOccurrencesMeta(data.meta || {})
      }
    } catch (error) {
      console.error('Failed to load occurrences:', error)
    } finally {
      setLoadingOccurrences(false)
    }
  }

  const goToSearchWithKeyword = (keyword) => {
    navigate(`/stats?tab=calls&search=${encodeURIComponent(keyword)}`)
  }

  // Audio player setup
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio()
    }
    const audio = audioRef.current

    const handleEnded = () => setPlayingRecording(null)
    const handleError = () => {
      setPlayingRecording(null)
      setAudioLoading(false)
    }
    const handlePlaying = () => setAudioLoading(false)

    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('playing', handlePlaying)

    return () => {
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('playing', handlePlaying)
      audio.pause()
    }
  }, [])

  // Stop audio when modal closes
  useEffect(() => {
    if (!showOccurrencesModal && audioRef.current) {
      audioRef.current.pause()
      setPlayingRecording(null)
    }
  }, [showOccurrencesModal])

  const toggleRecording = async (url) => {
    if (!audioRef.current || !url) return

    if (playingRecording === url) {
      audioRef.current.pause()
      setPlayingRecording(null)
    } else {
      setAudioLoading(true)
      audioRef.current.src = url
      try {
        await audioRef.current.play()
        setPlayingRecording(url)
      } catch (err) {
        console.error('Playback error:', err)
        setAudioLoading(false)
      }
    }
  }

  const buildDuplicateMap = (cats) => {
    const keywordMap = new Map()
    cats.forEach(cat => {
      (cat.keywords || []).forEach(kw => {
        const text = kw.text.toLowerCase()
        if (!keywordMap.has(text)) keywordMap.set(text, [])
        if (!keywordMap.get(text).includes(cat.name)) {
          keywordMap.get(text).push(cat.name)
        }
      })
    })
    setDuplicateKeywords(new Map(
      Array.from(keywordMap.entries()).filter(([_, cats]) => cats.length > 1)
    ))
  }

  // ========== HELPER FUNCTIONS ==========
  const getKeywordOccurrences = (keywordText) => {
    const stats = keywordStats[keywordText?.toLowerCase()]
    return stats?.total_matches || 0
  }

  // Check if keyword already exists in any category
  const isKeywordInDatabase = (text) => {
    if (!text) return true
    const normalized = text.toLowerCase().trim()
    if (dismissedSuggestions.has(normalized)) return true
    return categories.some(cat =>
      (cat.keywords || []).some(kw => kw.text.toLowerCase() === normalized)
    )
  }

  // Dismiss a suggestion permanently (saves to database)
  const dismissSuggestion = async (text) => {
    try {
      // Immediately hide it locally
      setDismissedSuggestions(prev => new Set([...prev, text.toLowerCase()]))

      // Persist to database
      await fetch(`${API_BASE_URL}/keyword-categories/suggestions/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phrase: text })
      })
    } catch (error) {
      console.error('Failed to dismiss suggestion:', error)
    }
  }

  const isRecent = (date) => {
    if (!date) return false
    const d = new Date(date)
    const now = new Date()
    const diffDays = (now - d) / (1000 * 60 * 60 * 24)
    return diffDays <= RECENT_DAYS
  }

  const isDuplicate = (keyword) => duplicateKeywords.has(keyword.text.toLowerCase())
  const getDuplicateCategories = (keyword) => duplicateKeywords.get(keyword.text.toLowerCase()) || []

  const getScoreClass = (score) => {
    if (score <= 2) return 'score-low'
    if (score <= 5) return 'score-medium'
    if (score <= 8) return 'score-high'
    return 'score-critical'
  }

  const getActionIcon = (action) => {
    switch (action) {
      case 'transfer': return <Phone size={14} />
      case 'hangup': return <PhoneOff size={14} />
      default: return <Play size={14} />
    }
  }

  // ========== COMPUTED STATS ==========
  const stats = useMemo(() => {
    const totalKeywords = categories.reduce((sum, cat) => sum + (cat.keywords?.length || 0), 0)
    const recentCount = categories.reduce((sum, cat) =>
      sum + (cat.keywords || []).filter(kw => isRecent(kw.created_at)).length, 0
    )
    const duplicateCount = duplicateKeywords.size
    const byAction = {
      continue: categories.filter(c => c.action === 'continue').length,
      transfer: categories.filter(c => c.action === 'transfer').length,
      hangup: categories.filter(c => c.action === 'hangup').length
    }
    return { totalKeywords, recentCount, duplicateCount, byAction }
  }, [categories, duplicateKeywords])

  // ========== FILTERING & SORTING ==========
  const filteredCategories = useMemo(() => {
    let result = [...categories]

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(cat =>
        cat.name.toLowerCase().includes(search) ||
        (cat.keywords || []).some(kw => kw.text.toLowerCase().includes(search))
      )
    }

    if (actionFilter !== 'all') {
      result = result.filter(cat => cat.action === actionFilter)
    }

    if (showDuplicatesOnly) {
      result = result.filter(cat =>
        (cat.keywords || []).some(kw => isDuplicate(kw))
      )
    }

    result.sort((a, b) => {
      let comparison = 0
      switch (sortBy) {
        case 'keywords':
          comparison = (b.keywords?.length || 0) - (a.keywords?.length || 0)
          break
        case 'threshold':
          comparison = a.threshold - b.threshold
          break
        case 'action':
          comparison = a.action.localeCompare(b.action)
          break
        case 'duplicates':
          const aDups = (a.keywords || []).filter(kw => isDuplicate(kw)).length
          const bDups = (b.keywords || []).filter(kw => isDuplicate(kw)).length
          comparison = bDups - aDups
          break
        default:
          comparison = a.name.localeCompare(b.name)
      }
      return sortDir === 'desc' ? -comparison : comparison
    })

    return result
  }, [categories, searchTerm, actionFilter, showDuplicatesOnly, sortBy, sortDir, duplicateKeywords])

  // ========== SUGGESTIONS TAB - FILTERED LIST ==========
  const filteredSuggestions = useMemo(() => {
    let all = [
      ...(suggestions.high || []).map(s => ({ ...s, priority: 'high' })),
      ...(suggestions.medium || []).map(s => ({ ...s, priority: 'medium' })),
      ...(suggestions.low || []).map(s => ({ ...s, priority: 'low' }))
    ].filter(s => !isKeywordInDatabase(s.text))

    // Filter by search
    if (suggestionsSearch.trim()) {
      const search = suggestionsSearch.toLowerCase()
      all = all.filter(s => s.text.toLowerCase().includes(search))
    }

    // Filter by priority
    if (priorityFilter !== 'all') {
      all = all.filter(s => s.priority === priorityFilter)
    }

    // Sort
    if (suggestionsSortBy === 'occurrences') {
      all.sort((a, b) => b.occurrences - a.occurrences)
    } else if (suggestionsSortBy === 'words') {
      all.sort((a, b) => b.word_count - a.word_count)
    } else if (suggestionsSortBy === 'alphabetical') {
      all.sort((a, b) => a.text.localeCompare(b.text))
    }

    return all
  }, [suggestions, suggestionsSearch, priorityFilter, suggestionsSortBy, dismissedSuggestions, categories])

  const suggestionsStats = useMemo(() => ({
    total: filteredSuggestions.length,
    high: (suggestions.high || []).filter(s => !isKeywordInDatabase(s.text)).length,
    medium: (suggestions.medium || []).filter(s => !isKeywordInDatabase(s.text)).length,
    low: (suggestions.low || []).filter(s => !isKeywordInDatabase(s.text)).length
  }), [suggestions, filteredSuggestions, dismissedSuggestions, categories])

  // ========== CATEGORY CRUD ==========
  const openNewCategoryModal = () => {
    setEditingCategory(null)
    setCategoryForm({ name: '', description: '', action: 'continue', disposition_code: '', threshold: 6, background_check: false })
    setShowCategoryModal(true)
  }

  const openEditCategoryModal = (category, e) => {
    e?.stopPropagation()
    setEditingCategory(category)
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      action: category.action,
      disposition_code: category.disposition_code || '',
      threshold: category.threshold,
      background_check: category.background_check || false
    })
    setShowCategoryModal(true)
  }

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) return
    setSaving(true)
    try {
      const payload = { ...categoryForm }
      let url = `${API_BASE_URL}/keyword-categories`
      let method = 'POST'

      if (editingCategory) {
        url = `${API_BASE_URL}/keyword-categories/${editingCategory.id}`
        method = 'PUT'
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (res.ok) {
        await loadCategories()
        setShowCategoryModal(false)
      }
    } catch (error) {
      console.error('Failed to save category:', error)
    } finally {
      setSaving(false)
    }
  }

  const deleteCategory = (category, e) => {
    e?.stopPropagation()
    setDeleteConfirmModal({ show: true, category })
    setDeletePassword('')
    setDeleteError('')
  }

  const confirmDeleteCategory = async () => {
    if (deletePassword !== DELETE_PASSWORD) {
      setDeleteError('Incorrect password')
      return
    }
    try {
      await fetch(`${API_BASE_URL}/keyword-categories/${deleteConfirmModal.category.id}`, { method: 'DELETE' })
      await loadCategories()
      setDeleteConfirmModal({ show: false, category: null })
      setDeletePassword('')
    } catch (error) {
      console.error('Failed to delete category:', error)
      setDeleteError('Failed to delete category')
    }
  }

  // ========== KEYWORD CRUD ==========
  const startAddingKeyword = (categoryId, e) => {
    e?.stopPropagation()
    setAddingKeywordToCategoryId(categoryId)
    setNewKeywordText('')
    setNewKeywordScore(5)
    setTimeout(() => newKeywordInputRef.current?.focus(), 100)
  }

  const cancelAddingKeyword = () => {
    setAddingKeywordToCategoryId(null)
    setNewKeywordText('')
  }

  const saveNewKeyword = async (categoryId) => {
    if (!newKeywordText.trim()) return
    try {
      const res = await fetch(`${API_BASE_URL}/keyword-categories/${categoryId}/keywords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newKeywordText.trim(), score: newKeywordScore })
      })
      if (res.ok) {
        await loadCategories()
        setNewKeywordText('')
      }
    } catch (error) {
      console.error('Failed to add keyword:', error)
    }
  }

  const startEditingKeyword = (keyword) => {
    setEditingKeywordId(keyword.id)
    setEditingKeywordText(keyword.text)
    setEditingKeywordScore(keyword.score)
  }

  const cancelEditingKeyword = () => {
    setEditingKeywordId(null)
    setEditingKeywordText('')
    setEditingKeywordScore(1)
  }

  const saveEditedKeyword = async (categoryId, keywordId) => {
    if (!editingKeywordText.trim()) return
    try {
      await fetch(`${API_BASE_URL}/keyword-categories/${categoryId}/keywords/${keywordId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: editingKeywordText.trim(), score: editingKeywordScore })
      })
      await loadCategories()
      cancelEditingKeyword()
    } catch (error) {
      console.error('Failed to update keyword:', error)
    }
  }

  const deleteKeyword = async (categoryId, keyword, e) => {
    e?.stopPropagation()
    try {
      await fetch(`${API_BASE_URL}/keyword-categories/${categoryId}/keywords/${keyword.id}`, {
        method: 'DELETE'
      })
      await loadCategories()
    } catch (error) {
      console.error('Failed to delete keyword:', error)
    }
  }

  // ========== SUGGESTION HANDLING (NEW) ==========
  const addSuggestionToCategory = async (suggestionText, categoryId, score = 5) => {
    try {
      const res = await fetch(`${API_BASE_URL}/keyword-categories/${categoryId}/keywords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: suggestionText, score: score })
      })
      if (res.ok) {
        await loadCategories()
        await loadStats()
        setAddingSuggestion(null)
        setSuggestionTargetCategory(null)
        setSuggestionScore(5)
      }
    } catch (error) {
      console.error('Failed to add suggestion:', error)
    }
  }

  // ========== RENDER ==========
  if (loading) {
    return (
      <div className="keyword-categories-page compact">
        <div className="loading-state">
          <Loader2 size={40} className="spin" />
          <p>Loading keyword categories...</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div className="keyword-categories-page compact" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* ========== HEADER ========== */}
      <div className="kw-header">
        <div className="kw-header-top">
          <div className="kw-title">
            <Key size={24} />
            <div>
              <h1>Keyword Categories</h1>
              <p className="kw-subtitle">{categories.length} categories • {stats.totalKeywords} keywords</p>
            </div>
          </div>
          <div className="kw-header-actions">
            <button className="btn btn-ghost" onClick={() => { loadCategories(); loadStats() }} title="Refresh">
              <RefreshCw size={18} className={loadingStats ? 'spin' : ''} />
            </button>
            {activeTab === 'categories' && (
              <button className="btn btn-primary" onClick={openNewCategoryModal}>
                <Plus size={18} /> New Category
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="kw-tabs">
          <button
            className={`kw-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            <List size={16} />
            Categories
            <span className="kw-tab-count">{categories.length}</span>
          </button>
          <button
            className={`kw-tab ${activeTab === 'suggestions' ? 'active' : ''}`}
            onClick={() => setActiveTab('suggestions')}
          >
            <Sparkles size={16} />
            Suggestions
            <span className="kw-tab-count">{suggestionsStats.total}</span>
          </button>
        </div>

      </div>

      {/* ========== CATEGORIES TAB CONTENT ========== */}
      {activeTab === 'categories' && (
        <>
          {/* Stats Row */}
          <div className="kw-stats-grid">
            <div className="kw-stat-card">
              <div className="kw-stat-icon"><Hash size={18} /></div>
              <div className="kw-stat-info">
                <span className="kw-stat-value">{stats.totalKeywords}</span>
                <span className="kw-stat-label">Total Keywords</span>
              </div>
            </div>
            <div className="kw-stat-card">
              <div className="kw-stat-icon success"><Zap size={18} /></div>
              <div className="kw-stat-info">
                <span className="kw-stat-value">{stats.recentCount}</span>
                <span className="kw-stat-label">New (7 days)</span>
              </div>
            </div>
            <div className={`kw-stat-card ${stats.duplicateCount > 0 ? 'warning' : ''}`}>
              <div className="kw-stat-icon warning"><Copy size={18} /></div>
              <div className="kw-stat-info">
                <span className="kw-stat-value">{stats.duplicateCount}</span>
                <span className="kw-stat-label">Duplicates</span>
              </div>
            </div>
            <div className="kw-stat-card">
              <div className="kw-stat-icon info"><Lightbulb size={18} /></div>
              <div className="kw-stat-info">
                <span className="kw-stat-value">{suggestionsStats.total}</span>
                <span className="kw-stat-label">Suggestions</span>
              </div>
            </div>
            <div className="kw-stat-card-actions">
              <button className={`kw-action-filter ${actionFilter === 'all' ? 'active' : ''}`} onClick={() => setActionFilter('all')}>All</button>
              <button className={`kw-action-filter ${actionFilter === 'continue' ? 'active continue' : ''}`} onClick={() => setActionFilter('continue')}>
                <Play size={12} /> {stats.byAction.continue}
              </button>
              <button className={`kw-action-filter ${actionFilter === 'transfer' ? 'active transfer' : ''}`} onClick={() => setActionFilter('transfer')}>
                <Phone size={12} /> {stats.byAction.transfer}
              </button>
              <button className={`kw-action-filter ${actionFilter === 'hangup' ? 'active hangup' : ''}`} onClick={() => setActionFilter('hangup')}>
                <PhoneOff size={12} /> {stats.byAction.hangup}
              </button>
            </div>
          </div>

          {/* ========== TOOLBAR ========== */}
      <div className="kw-toolbar">
        <div className="kw-toolbar-left">
          <div className="kw-search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search categories or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button onClick={() => setSearchTerm('')}><X size={14} /></button>}
          </div>
          {showDuplicatesOnly && (
            <div className="kw-active-filter">
              <AlertTriangle size={12} /> Showing duplicates only
              <button onClick={() => setShowDuplicatesOnly(false)}><X size={12} /></button>
            </div>
          )}
        </div>
        <div className="kw-toolbar-right">
          <select className="kw-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Sort: Name</option>
            <option value="keywords">Sort: Keywords</option>
            <option value="threshold">Sort: Threshold</option>
            <option value="action">Sort: Action</option>
            <option value="duplicates">Sort: Duplicates</option>
          </select>
          <button className="btn btn-ghost btn-xs" onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}>
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
          <button
            className={`btn btn-ghost btn-xs ${showDuplicatesOnly ? 'active' : ''}`}
            onClick={() => setShowDuplicatesOnly(!showDuplicatesOnly)}
            title="Show duplicates only"
          >
            <Copy size={14} />
          </button>
        </div>
      </div>

      {/* ========== CATEGORIES LIST ========== */}
      {filteredCategories.length === 0 ? (
        <div className="empty-state">
          <Key size={48} />
          <h3>No categories found</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Create your first keyword category'}</p>
          {!searchTerm && <button className="btn btn-primary" onClick={openNewCategoryModal}><Plus size={16} /> Create Category</button>}
        </div>
      ) : (
        <div className="kw-categories-list">
          {filteredCategories.map(category => {
            const isExpanded = expandedCategoryId === category.id
            // Filter keywords, then sort by occurrences (most first)
            const filteredKeywords = (keywordSearchTerm && isExpanded
              ? (category.keywords || []).filter(kw => kw.text.toLowerCase().includes(keywordSearchTerm.toLowerCase()))
              : (category.keywords || [])
            ).sort((a, b) => {
              const aOcc = getKeywordOccurrences(a.text)
              const bOcc = getKeywordOccurrences(b.text)
              if (bOcc !== aOcc) return bOcc - aOcc // Most occurrences first
              return b.score - a.score // Then by score
            })
            const showAll = showAllKeywords[category.id]
            const displayKeywords = showAll ? filteredKeywords : filteredKeywords.slice(0, KEYWORDS_INITIAL_SHOW)
            const hasMore = filteredKeywords.length > KEYWORDS_INITIAL_SHOW
            const categoryDuplicates = (category.keywords || []).filter(kw => isDuplicate(kw)).length

            return (
              <div key={category.id} className={`kw-category-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="kw-category-header" onClick={() => setExpandedCategoryId(isExpanded ? null : category.id)}>
                  <div className="kw-category-left">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <span className={`kw-action-badge ${category.action}`}>
                      {getActionIcon(category.action)}
                    </span>
                    <h3 className="kw-category-name">{category.name}</h3>
                    <span className="kw-keyword-count">{category.keywords?.length || 0}</span>
                    {categoryDuplicates > 0 && <span className="kw-dup-badge" title={`${categoryDuplicates} duplicate keywords`}>{categoryDuplicates}</span>}
                  </div>
                  <div className="kw-category-right">
                    <div className="kw-threshold-mini">
                      <Zap size={12} />
                      <span>{category.threshold}</span>
                    </div>
                    <button className="btn btn-ghost btn-xs" onClick={(e) => openEditCategoryModal(category, e)}><Edit size={14} /></button>
                    <button className="btn btn-ghost btn-xs danger" onClick={(e) => deleteCategory(category, e)}><Trash2 size={14} /></button>
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      className="kw-category-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <div className="kw-keyword-toolbar">
                        <div className="kw-keyword-search">
                          <Search size={14} />
                          <input
                            type="text"
                            placeholder="Filter keywords..."
                            value={keywordSearchTerm}
                            onChange={(e) => setKeywordSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="kw-keyword-actions-bar">
                          <button className="btn btn-primary btn-xs" onClick={(e) => startAddingKeyword(category.id, e)}>
                            <Plus size={12} /> Add Keyword
                          </button>
                        </div>
                      </div>

                      {addingKeywordToCategoryId === category.id && (
                        <div className="kw-add-keyword-inline">
                          <input
                            ref={newKeywordInputRef}
                            type="text"
                            placeholder="New keyword..."
                            value={newKeywordText}
                            onChange={(e) => setNewKeywordText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveNewKeyword(category.id); if (e.key === 'Escape') cancelAddingKeyword() }}
                            className="kw-input"
                          />
                          <div className="kw-score-control">
                            <input type="range" min="1" max="10" value={newKeywordScore} onChange={(e) => setNewKeywordScore(parseInt(e.target.value))} />
                            <span className={`kw-score-badge ${getScoreClass(newKeywordScore)}`}>{newKeywordScore}</span>
                          </div>
                          <button className="btn btn-success btn-xs" onClick={() => saveNewKeyword(category.id)}><Check size={12} /></button>
                          <button className="btn btn-secondary btn-xs" onClick={cancelAddingKeyword}><X size={12} /></button>
                        </div>
                      )}

                      <div className="kw-keywords-grid">
                        {displayKeywords.length === 0 ? (
                          <p className="kw-no-keywords">
                            {keywordSearchTerm ? 'No matching keywords' : 'No keywords yet'}
                          </p>
                        ) : (
                          displayKeywords.map(keyword => {
                            const occurrences = getKeywordOccurrences(keyword.text)
                            return (
                              <div
                                key={keyword.id}
                                className={`kw-keyword-item ${isDuplicate(keyword) ? 'duplicate' : ''} ${isRecent(keyword.created_at) ? 'recent' : ''} ${editingKeywordId === keyword.id ? 'editing' : ''}`}
                              >
                                {editingKeywordId !== keyword.id ? (
                                  <>
                                    <span className={`kw-score-badge ${getScoreClass(keyword.score)}`}>{keyword.score}</span>
                                    <span className="kw-keyword-text">{keyword.text}</span>
                                    {occurrences > 0 && (
                                      <button
                                        className="kw-occurrence-badge"
                                        onClick={(e) => { e.stopPropagation(); loadOccurrences(keyword.text) }}
                                        title={`${occurrences.toLocaleString()} matches in last 7 days - Click to view`}
                                      >
                                        {formatCount(occurrences)}×
                                      </button>
                                    )}
                                    {isRecent(keyword.created_at) && <span className="kw-badge new">New</span>}
                                    {isDuplicate(keyword) && (
                                      <span className="kw-badge dup" title={`Also in: ${getDuplicateCategories(keyword).filter(c => c !== category.name).join(', ')}`}>
                                        Dup
                                      </span>
                                    )}
                                    <div className="kw-keyword-actions">
                                      <button onClick={() => startEditingKeyword(keyword)}><Edit size={10} /></button>
                                      <button className="danger" onClick={(e) => deleteKeyword(category.id, keyword, e)}><Trash2 size={10} /></button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <input
                                      type="text"
                                      value={editingKeywordText}
                                      onChange={(e) => setEditingKeywordText(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') saveEditedKeyword(category.id, keyword.id); if (e.key === 'Escape') cancelEditingKeyword() }}
                                      className="kw-input sm"
                                      autoFocus
                                    />
                                    <div className="kw-score-control sm">
                                      <input type="range" min="1" max="10" value={editingKeywordScore} onChange={(e) => setEditingKeywordScore(parseInt(e.target.value))} />
                                      <span className={`kw-score-badge ${getScoreClass(editingKeywordScore)}`}>{editingKeywordScore}</span>
                                    </div>
                                    <button className="btn btn-success btn-xs" onClick={() => saveEditedKeyword(category.id, keyword.id)}><Check size={10} /></button>
                                    <button className="btn btn-secondary btn-xs" onClick={cancelEditingKeyword}><X size={10} /></button>
                                  </>
                                )}
                              </div>
                            )
                          })
                        )}
                      </div>

                      {hasMore && !showAll && (
                        <button className="kw-show-more" onClick={() => setShowAllKeywords({ ...showAllKeywords, [category.id]: true })}>
                          <Eye size={12} /> Show all {filteredKeywords.length} keywords ({filteredKeywords.length - KEYWORDS_INITIAL_SHOW} more)
                        </button>
                      )}
                      {hasMore && showAll && (
                        <button className="kw-show-more" onClick={() => setShowAllKeywords({ ...showAllKeywords, [category.id]: false })}>
                          <EyeOff size={12} /> Show less
                        </button>
                      )}

                      {category.description && (
                        <div className="kw-category-description">ℹ️ {category.description}</div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      )}
        </>
      )}

      {/* ========== SUGGESTIONS TAB CONTENT ========== */}
      {activeTab === 'suggestions' && (
        <div className="ks-page">
          {/* Compact Header */}
          <div className="ks-header">
            <div className="ks-header-stats">
              <span className="ks-stat-pill total"><Hash size={12} /> {suggestionsStats.total} Total</span>
              <span className="ks-stat-pill high"><Zap size={12} /> {suggestionsStats.high} High</span>
              <span className="ks-stat-pill medium"><Target size={12} /> {suggestionsStats.medium} Medium</span>
              <span className="ks-stat-pill low"><TrendingUp size={12} /> {suggestionsStats.low} Low</span>
            </div>
            <div className="ks-header-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Filter suggestions..."
                value={suggestionsSearch}
                onChange={(e) => setSuggestionsSearch(e.target.value)}
              />
              {suggestionsSearch && <button onClick={() => setSuggestionsSearch('')}><X size={12} /></button>}
            </div>
          </div>

          {loadingStats ? (
            <div className="ks-loading">
              <Loader2 size={28} className="spin" />
              <p>Loading suggestions...</p>
            </div>
          ) : (
            <div className="ks-sections">
              {/* High Priority Section */}
              {(suggestions.high || []).filter(s => !isKeywordInDatabase(s.text) && (!suggestionsSearch || s.text.toLowerCase().includes(suggestionsSearch.toLowerCase()))).length > 0 && (
                <div className="ks-section high">
                  <div className="ks-section-header">
                    <Zap size={16} />
                    <span>High Priority</span>
                    <span className="ks-section-count">{(suggestions.high || []).filter(s => !isKeywordInDatabase(s.text)).length}</span>
                    <span className="ks-section-hint">100+ occurrences</span>
                  </div>
                  <div className="ks-grid">
                    {(suggestions.high || [])
                      .filter(s => !isKeywordInDatabase(s.text) && (!suggestionsSearch || s.text.toLowerCase().includes(suggestionsSearch.toLowerCase())))
                      .slice(0, 30)
                      .map((s, i) => (
                        <SuggestionChip
                          key={`high-${i}`}
                          suggestion={s}
                          isAdding={addingSuggestion === s.text}
                          categories={categories}
                          targetCategory={suggestionTargetCategory}
                          suggestionScore={suggestionScore}
                          onStartAdd={() => { setAddingSuggestion(s.text); setSuggestionTargetCategory(null); setSuggestionScore(5) }}
                          onSelectCategory={(catId) => setSuggestionTargetCategory(catId)}
                          onScoreChange={(score) => setSuggestionScore(score)}
                          onConfirmAdd={() => addSuggestionToCategory(s.text, suggestionTargetCategory, suggestionScore)}
                          onCancel={() => { setAddingSuggestion(null); setSuggestionTargetCategory(null) }}
                          onDismiss={() => dismissSuggestion(s.text)}
                          onViewOccurrences={loadOccurrences}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Medium Priority Section */}
              {(suggestions.medium || []).filter(s => !isKeywordInDatabase(s.text) && (!suggestionsSearch || s.text.toLowerCase().includes(suggestionsSearch.toLowerCase()))).length > 0 && (
                <div className="ks-section medium">
                  <div className="ks-section-header">
                    <Target size={16} />
                    <span>Medium Priority</span>
                    <span className="ks-section-count">{(suggestions.medium || []).filter(s => !isKeywordInDatabase(s.text)).length}</span>
                    <span className="ks-section-hint">50-99 occurrences</span>
                  </div>
                  <div className="ks-grid">
                    {(suggestions.medium || [])
                      .filter(s => !isKeywordInDatabase(s.text) && (!suggestionsSearch || s.text.toLowerCase().includes(suggestionsSearch.toLowerCase())))
                      .slice(0, 30)
                      .map((s, i) => (
                        <SuggestionChip
                          key={`medium-${i}`}
                          suggestion={s}
                          isAdding={addingSuggestion === s.text}
                          categories={categories}
                          targetCategory={suggestionTargetCategory}
                          suggestionScore={suggestionScore}
                          onStartAdd={() => { setAddingSuggestion(s.text); setSuggestionTargetCategory(null); setSuggestionScore(5) }}
                          onSelectCategory={(catId) => setSuggestionTargetCategory(catId)}
                          onScoreChange={(score) => setSuggestionScore(score)}
                          onConfirmAdd={() => addSuggestionToCategory(s.text, suggestionTargetCategory, suggestionScore)}
                          onCancel={() => { setAddingSuggestion(null); setSuggestionTargetCategory(null) }}
                          onDismiss={() => dismissSuggestion(s.text)}
                          onViewOccurrences={loadOccurrences}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Low Priority Section */}
              {(suggestions.low || []).filter(s => !isKeywordInDatabase(s.text) && (!suggestionsSearch || s.text.toLowerCase().includes(suggestionsSearch.toLowerCase()))).length > 0 && (
                <div className="ks-section low">
                  <div className="ks-section-header">
                    <TrendingUp size={16} />
                    <span>Low Priority</span>
                    <span className="ks-section-count">{(suggestions.low || []).filter(s => !isKeywordInDatabase(s.text)).length}</span>
                    <span className="ks-section-hint">10-49 occurrences</span>
                  </div>
                  <div className="ks-grid">
                    {(suggestions.low || [])
                      .filter(s => !isKeywordInDatabase(s.text) && (!suggestionsSearch || s.text.toLowerCase().includes(suggestionsSearch.toLowerCase())))
                      .slice(0, 30)
                      .map((s, i) => (
                        <SuggestionChip
                          key={`low-${i}`}
                          suggestion={s}
                          isAdding={addingSuggestion === s.text}
                          categories={categories}
                          targetCategory={suggestionTargetCategory}
                          suggestionScore={suggestionScore}
                          onStartAdd={() => { setAddingSuggestion(s.text); setSuggestionTargetCategory(null); setSuggestionScore(5) }}
                          onSelectCategory={(catId) => setSuggestionTargetCategory(catId)}
                          onScoreChange={(score) => setSuggestionScore(score)}
                          onConfirmAdd={() => addSuggestionToCategory(s.text, suggestionTargetCategory, suggestionScore)}
                          onCancel={() => { setAddingSuggestion(null); setSuggestionTargetCategory(null) }}
                          onDismiss={() => dismissSuggestion(s.text)}
                          onViewOccurrences={loadOccurrences}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {suggestionsStats.total === 0 && (
                <div className="ks-empty">
                  <Sparkles size={40} />
                  <h3>All caught up!</h3>
                  <p>All suggestions have been added or dismissed.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== CATEGORY MODAL ========== */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCategoryModal(false)}>
            <motion.div className="modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingCategory ? <><Edit size={18} /> Edit Category</> : <><Plus size={18} /> Create Category</>}</h2>
                <button className="modal-close" onClick={() => setShowCategoryModal(false)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <div className="input-group">
                  <label className="input-label">Category Name *</label>
                  <input type="text" className="input" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="e.g., Do Not Call, Transfer Interest" autoFocus />
                </div>
                <div className="input-group">
                  <label className="input-label">Description</label>
                  <input type="text" className="input" value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} placeholder="Optional description" />
                </div>
                <div className="input-group">
                  <label className="input-label">Action</label>
                  <div className="kw-action-buttons">
                    <button type="button" className={`kw-action-btn ${categoryForm.action === 'continue' ? 'active continue' : ''}`} onClick={() => setCategoryForm({ ...categoryForm, action: 'continue' })}>
                      <Play size={14} /> Continue
                    </button>
                    <button type="button" className={`kw-action-btn ${categoryForm.action === 'transfer' ? 'active transfer' : ''}`} onClick={() => setCategoryForm({ ...categoryForm, action: 'transfer' })}>
                      <Phone size={14} /> Transfer
                    </button>
                    <button type="button" className={`kw-action-btn ${categoryForm.action === 'hangup' ? 'active hangup' : ''}`} onClick={() => setCategoryForm({ ...categoryForm, action: 'hangup' })}>
                      <PhoneOff size={14} /> Hangup
                    </button>
                  </div>
                </div>
                {categoryForm.action === 'hangup' && (
                  <div className="input-group">
                    <label className="input-label">Disposition Code</label>
                    <select className="input" value={categoryForm.disposition_code} onChange={(e) => setCategoryForm({ ...categoryForm, disposition_code: e.target.value })}>
                      <option value="">-- Select Disposition --</option>
                      {DISPOSITION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                )}
                <div className="input-group">
                  <label className="input-label">Score Threshold: <strong>{categoryForm.threshold}</strong></label>
                  <input type="range" min="1" max="20" value={categoryForm.threshold} onChange={(e) => setCategoryForm({ ...categoryForm, threshold: parseInt(e.target.value) })} className="kw-threshold-slider" />
                  <small className="form-text">Action triggers when cumulative keyword score reaches this threshold</small>
                </div>
                <div className="input-group">
                  <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={categoryForm.background_check}
                      onChange={(e) => setCategoryForm({ ...categoryForm, background_check: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Check during background speech</span>
                  </label>
                  <small className="form-text">
                    When enabled, this category is checked even while the bot is talking.
                    Use for critical keywords like DNC, Answering Machine, Not Interested.
                    When disabled, category only checked after bot finishes speaking.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCategoryModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveCategory} disabled={saving || !categoryForm.name.trim()}>
                  {saving ? <Loader2 size={16} className="spin" /> : editingCategory ? <Check size={16} /> : <Plus size={16} />}
                  {editingCategory ? 'Save Changes' : 'Create Category'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== OCCURRENCES MODAL ========== */}
      <AnimatePresence>
        {showOccurrencesModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowOccurrencesModal(false)}
          >
            <motion.div
              className="modal occ-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="occ-modal-title">
                  <MessageSquare size={20} />
                  <div>
                    <h2>Keyword Occurrences</h2>
                    <span className="occ-keyword-highlight">"{occurrencesKeyword}"</span>
                  </div>
                </div>
                <button className="modal-close" onClick={() => setShowOccurrencesModal(false)}><X size={20} /></button>
              </div>

              <div className="modal-body occ-modal-body">
                {loadingOccurrences ? (
                  <div className="occ-loading">
                    <Loader2 size={32} className="spin" />
                    <p>Searching transcriptions...</p>
                  </div>
                ) : occurrencesData.length === 0 ? (
                  <div className="occ-empty">
                    <FileText size={40} />
                    <p>No occurrences found in the last 7 days</p>
                  </div>
                ) : (
                  <>
                    <div className="occ-stats-row">
                      <div className="occ-stat">
                        <span className="occ-stat-value">{occurrencesMeta?.total_occurrences || 0}</span>
                        <span className="occ-stat-label">Occurrences</span>
                      </div>
                      <div className="occ-stat">
                        <span className="occ-stat-value">{occurrencesMeta?.unique_calls || 0}</span>
                        <span className="occ-stat-label">Unique Calls</span>
                      </div>
                      <div className="occ-stat">
                        <span className="occ-stat-value">{occurrencesMeta?.days_searched || 7}d</span>
                        <span className="occ-stat-label">Period</span>
                      </div>
                    </div>

                    <div className="occ-info-banner">
                      <Info size={14} />
                      <span>Showing max 200 occurrences from last 7 days for performance</span>
                    </div>

                    <div className="occ-list">
                      {occurrencesData.map((occ, i) => (
                        <div key={`${occ.call_id}-${i}`} className={`occ-item ${playingRecording === occ.recording_url ? 'playing' : ''}`}>
                          <div className="occ-item-header">
                            <span className="occ-item-category">{occ.keyword_category}</span>
                            <span className="occ-item-time">
                              <Clock size={12} />
                              {new Date(occ.match_time).toLocaleString('en-US', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="occ-item-snippet">
                            <HighlightedText text={occ.transcription_snippet || ''} highlight={occurrencesKeyword} />
                          </div>
                          <div className="occ-item-meta">
                            {occ.recording_url ? (
                              <button
                                className={`occ-play-btn ${playingRecording === occ.recording_url ? 'playing' : ''} ${audioLoading && playingRecording !== occ.recording_url ? '' : ''}`}
                                onClick={() => toggleRecording(occ.recording_url)}
                                title={occ.recording_duration ? `${occ.recording_duration}s recording` : 'Play recording'}
                              >
                                {playingRecording === occ.recording_url ? <Pause size={12} /> : <Play size={12} />}
                                {occ.recording_duration && <span>{occ.recording_duration}s</span>}
                              </button>
                            ) : (
                              <span className="occ-no-recording" title="No recording available">
                                <Volume2 size={12} />
                              </span>
                            )}
                            <span className="occ-meta-item">
                              <Phone size={12} />
                              {occ.phone_number || 'Unknown'}
                            </span>
                            <span className="occ-meta-item">SIP: {occ.sip_id}</span>
                            <span className="occ-meta-item">Step: {occ.script_step}</span>
                            {occ.disposition_label && (
                              <span className={`occ-dispo ${occ.disposition?.includes('transfer') ? 'transfer' : ''}`}>
                                {occ.disposition_label}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer occ-modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowOccurrencesModal(false)}>
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowOccurrencesModal(false)
                    goToSearchWithKeyword(occurrencesKeyword)
                  }}
                >
                  <Search size={16} />
                  Search Call Logs
                  <ExternalLink size={14} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== DELETE CONFIRMATION MODAL ========== */}
      <AnimatePresence>
        {deleteConfirmModal.show && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDeleteConfirmModal({ show: false, category: null })}
          >
            <motion.div
              className="modal delete-confirm-modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header danger">
                <h2><Lock size={18} /> Confirm Delete</h2>
                <button className="modal-close" onClick={() => setDeleteConfirmModal({ show: false, category: null })}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="delete-warning">
                  <AlertTriangle size={24} />
                  <p>You are about to delete the category <strong>"{deleteConfirmModal.category?.name}"</strong> and all its keywords.</p>
                </div>
                <div className="input-group">
                  <label className="input-label">Enter password to confirm deletion</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Password"
                    value={deletePassword}
                    onChange={(e) => { setDeletePassword(e.target.value); setDeleteError('') }}
                    onKeyDown={(e) => e.key === 'Enter' && confirmDeleteCategory()}
                    autoFocus
                  />
                  {deleteError && <span className="error-text">{deleteError}</span>}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setDeleteConfirmModal({ show: false, category: null })}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteCategory}
                  disabled={!deletePassword}
                >
                  <Trash2 size={16} />
                  Delete Category
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Searchable Select Component - For dropdowns with many options
function SearchableSelect({ options, value, onChange, placeholder = "Select...", className = "" }) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [flipUp, setFlipUp] = useState(false)
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Check position and flip if near bottom
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const dropdownHeight = 300 // Approximate dropdown height

      // Flip up if not enough space below but enough above
      setFlipUp(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight)

      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }, [isOpen])

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  )

  const selectedOption = options.find(opt => opt.value === value)

  const handleSelect = (opt) => {
    onChange(opt.value)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <div className={`searchable-select ${className} ${flipUp ? 'flip-up' : ''}`} ref={containerRef}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedOption ? '' : 'placeholder'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={14} className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </div>

      {isOpen && (
        <div className={`searchable-select-dropdown ${flipUp ? 'flip-up' : ''}`}>
          <div className="searchable-select-search">
            <Search size={12} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type to search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {search && (
              <button className="clear-search" onClick={() => setSearch('')}>
                <X size={12} />
              </button>
            )}
          </div>
          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">No matches found</div>
            ) : (
              filteredOptions.map(opt => (
                <div
                  key={opt.value}
                  className={`searchable-select-option ${opt.value === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Suggestion Card Component - Compact with icons
function SuggestionCard({
  suggestion,
  categories,
  isAdding,
  targetCategory,
  suggestionScore,
  onStartAdd,
  onSelectCategory,
  onScoreChange,
  onConfirmAdd,
  onCancel,
  onDismiss,
  onViewOccurrences
}) {
  return (
    <motion.div
      className={`kw-sug-compact ${isAdding ? 'adding' : ''}`}
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
    >
      {!isAdding ? (
        <>
          <div className="kw-sug-main">
            <span className="kw-sug-text">{suggestion.text}</span>
            <span
              className="kw-sug-occ"
              onClick={() => onViewOccurrences && onViewOccurrences(suggestion.text)}
              title={`${suggestion.occurrences.toLocaleString()} occurrences - Click to view`}
            >
              {formatCount(suggestion.occurrences)}×
            </span>
          </div>
          <div className="kw-sug-actions">
            <button
              className="kw-sug-icon-btn add"
              onClick={onStartAdd}
              title="Add to category"
            >
              <Plus size={14} />
            </button>
            <button
              className="kw-sug-icon-btn view"
              onClick={() => onViewOccurrences && onViewOccurrences(suggestion.text)}
              title="View occurrences"
            >
              <Eye size={14} />
            </button>
            <button
              className="kw-sug-icon-btn dismiss"
              onClick={onDismiss}
              title="Dismiss suggestion"
            >
              <X size={14} />
            </button>
          </div>
        </>
      ) : (
        <div className="kw-sug-add-form">
          <div className="kw-sug-text highlight">{suggestion.text}</div>
          <SearchableSelect
            options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
            value={targetCategory}
            onChange={onSelectCategory}
            placeholder="Select category..."
            className="category-select-compact"
          />
          <div className="kw-sug-score-row">
            <span>Score:</span>
            <input
              type="range"
              min="1"
              max="10"
              value={suggestionScore}
              onChange={(e) => onScoreChange(parseInt(e.target.value))}
            />
            <span className="kw-sug-score-val">{suggestionScore}</span>
          </div>
          <div className="kw-sug-form-actions">
            <button className="kw-sug-icon-btn confirm" onClick={onConfirmAdd} disabled={!targetCategory}>
              <Check size={14} />
            </button>
            <button className="kw-sug-icon-btn cancel" onClick={onCancel}>
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  )
}

// Compact Suggestion Chip for grid layout
function SuggestionChip({
  suggestion,
  isAdding,
  categories,
  targetCategory,
  suggestionScore,
  onStartAdd,
  onSelectCategory,
  onScoreChange,
  onConfirmAdd,
  onCancel,
  onDismiss,
  onViewOccurrences
}) {
  if (isAdding) {
    return (
      <motion.div
        className="ks-chip adding"
        layout
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
      >
        <div className="ks-chip-text">{suggestion.text}</div>
        <SearchableSelect
          options={categories.map(cat => ({ value: cat.id, label: cat.name }))}
          value={targetCategory}
          onChange={onSelectCategory}
          placeholder="Select category..."
          className="ks-chip-select"
        />
        <div className="ks-chip-score">
          <input
            type="range"
            min="1"
            max="10"
            value={suggestionScore}
            onChange={(e) => onScoreChange(parseInt(e.target.value))}
          />
          <span>{suggestionScore}</span>
        </div>
        <div className="ks-chip-actions">
          <button className="confirm" onClick={onConfirmAdd} disabled={!targetCategory}><Check size={12} /></button>
          <button className="cancel" onClick={onCancel}><X size={12} /></button>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      className="ks-chip"
      layout
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
    >
      <span className="ks-chip-text">{suggestion.text}</span>
      <span className="ks-chip-occ" onClick={() => onViewOccurrences(suggestion.text)} title="View occurrences">
        {formatCount(suggestion.occurrences)}
      </span>
      <div className="ks-chip-btns">
        <button className="add" onClick={onStartAdd} title="Add to category"><Plus size={12} /></button>
        <button className="dismiss" onClick={onDismiss} title="Dismiss"><X size={12} /></button>
      </div>
    </motion.div>
  )
}

// Highlighted text component for occurrences
function HighlightedText({ text, highlight }) {
  if (!highlight || !text) return <span>{text}</span>
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase()
          ? <mark key={i} className="occ-highlight">{part}</mark>
          : part
      )}
    </span>
  )
}

export default KeywordCategories